import * as React from 'react';
import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import { cn } from '@/shared/lib/utils';

const cardVariants = cva(
  'relative overflow-hidden rounded-xl border bg-surface transition-all duration-300',
  {
    variants: {
      variant: {
        default: 'border-border shadow-card hover:shadow-elevated',
        elevated: 'border-border shadow-elevated',
        flat: 'border-border-subtle shadow-sm',
        interactive:
          'border-border shadow-card hover:shadow-elevated hover:-translate-y-0.5 cursor-pointer',
        highlighted:
          'border-primary/20 shadow-glow-primary bg-gradient-to-br from-surface to-surface-elevated',
      },
      padding: {
        none: 'p-0',
        sm: 'p-4',
        md: 'p-5',
        lg: 'p-6',
        xl: 'p-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      padding: 'md',
    },
  }
);

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
  gradient?: boolean;
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, padding, gradient, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(cardVariants({ variant, padding, className }))}
        {...props}
      >
        {gradient && (
          <div
            className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full opacity-30 blur-3xl"
            style={{
              background:
                'radial-gradient(circle, rgba(30,58,95,0.08) 0%, transparent 70%)',
            }}
          />
        )}
        <div className="relative z-10">{children}</div>
      </div>
    );
  }
);
Card.displayName = 'Card';

export const CardHeader = ({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => (
  <div className={cn('flex items-start justify-between gap-4', className)}>
    {children}
  </div>
);

export const CardTitle = ({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => (
  <h3
    className={cn(
      'text-base font-display font-semibold text-text-primary tracking-tight',
      className
    )}
  >
    {children}
  </h3>
);

export const CardDescription = ({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => (
  <p className={cn('text-sm text-text-secondary', className)}>{children}</p>
);

export const CardContent = ({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => <div className={cn('mt-4', className)}>{children}</div>;

export const CardFooter = ({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => (
  <div
    className={cn(
      'mt-4 flex items-center gap-2 border-t border-border-subtle pt-4',
      className
    )}
  >
    {children}
  </div>
);
